import { useMemo, useState } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";

/**
 * Lightweight sort hook. Returns sorted items + a SortableTh component
 * that can be used in any table header.
 *
 * Usage:
 *   const { sorted, SortableTh } = useTableSort(items, "name", "asc");
 *   <SortableTh field="name">Name</SortableTh>
 *
 * The optional `accessor` map lets you tweak how a field is read (e.g. compute
 * derived values). Default reads `row[field]`.
 */
export function useTableSort(items, defaultField = null, defaultDir = "asc", accessor = {}) {
  const [field, setField] = useState(defaultField);
  const [dir, setDir] = useState(defaultDir);

  const onToggle = (f) => {
    if (field === f) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setField(f);
      setDir("asc");
    }
  };

  const sorted = useMemo(() => {
    if (!field || !Array.isArray(items)) return items || [];
    const get = accessor[field] || ((row) => row?.[field]);
    const copy = [...items];
    copy.sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va == null && vb == null) return 0;
      if (va == null) return dir === "asc" ? 1 : -1;
      if (vb == null) return dir === "asc" ? -1 : 1;
      if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
      const sa = String(va).toLowerCase();
      const sb = String(vb).toLowerCase();
      if (sa === sb) return 0;
      return dir === "asc" ? (sa < sb ? -1 : 1) : (sa < sb ? 1 : -1);
    });
    return copy;
    // eslint-disable-next-line
  }, [items, field, dir]);

  const SortableTh = ({ field: f, children, className = "", align = "left" }) => {
    const active = field === f;
    const arrow = active ? (dir === "asc" ? <CaretUp size={10} /> : <CaretDown size={10} />) : null;
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <th className={`px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-600 ${className}`}>
        <button
          type="button"
          onClick={() => onToggle(f)}
          className={`flex items-center gap-1 w-full ${justify} hover:text-slate-900 cursor-pointer select-none`}
          data-testid={`sort-${f}`}
          title={active ? (dir === "asc" ? "Absteigend sortieren" : "Aufsteigend sortieren") : "Sortieren"}
        >
          <span>{children}</span>
          <span className={`text-slate-400 ${active ? "text-slate-900" : "opacity-40"}`}>
            {arrow || <CaretUp size={10} />}
          </span>
        </button>
      </th>
    );
  };

  return { sorted, field, dir, setField, setDir, SortableTh };
}
